import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePressAction } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { usePresence } from '../hooks/usePresence'; // [FLUID-1 2026-10-12]
import { fluid } from '../styles/motion'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade } from './Fluid'; // [FLUID-1 2026-10-12]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] the calendar is placed in page px
import { lgPopover, lgControl, lgPress, lgRim, lgControlBg, lgControlHover, lgCardSheen, lgGreen, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]

/**
 * SingleDateCalendar — a compact, single-date picker that matches the app's glass
 * theme. Unlike Looker's start/end range picker, this selects exactly ONE day.
 *
 * The popover is portaled to <body> and positioned `fixed` under the trigger, so
 * it never gets clipped by a scroll/`overflow` ancestor or mis-centred by the
 * PageFade transform (see design_modal_portals). It closes on outside-click, Esc,
 * scroll and resize to avoid drifting away from its anchor.
 */

interface Props {
  /** Selected date, YYYY-MM-DD. */
  value: string;
  /** If given, only these dates are selectable; all others are disabled. */
  availableDates?: string[];
  min?: string | null;
  max?: string | null;
  onChange: (date: string) => void;
  /** [NO-HINTS 2026-10-15] false = no "Pick a date" hover hint (Lead Management asked for none). Default: shown. */
  hint?: boolean;
  /** [LIQUID-GLASS 2026-11-02] true = the button is a glass capsule (light edge, shine, a squish and a glow where pressed), the
   *  glass dropdowns' look; its size stays. Lead Management, Team Sameer Report and Lead Generator Performance. Default: plain. */
  liquid?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toKey = (y: number, m0: number, d: number) => `${y}-${pad(m0 + 1)}-${pad(d)}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function parseKey(s: string): { y: number; m0: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  return { y: Number(m[1]), m0: Number(m[2]) - 1, d: Number(m[3]) };
}

function fmtLong(s: string): string {
  const p = parseKey(s);
  if (!p) return 'Select date';
  return `${MONTHS[p.m0].slice(0, 3)} ${p.d}, ${p.y}`;
}

/* ───────────────────────── trigger ───────────────────────── */

/* [LIQUID-GLASS 2026-11-02] marked for the press light: it shows only where a page gives the button the glass press (the DT
   Daily Report's Date Filter); everywhere else the mark changes nothing */
const Trigger = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $liquid?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.75)')};
  color: ${(p) => p.theme.text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  i.cal { color: ${(p) => p.theme.greenInk}; font-size: 13px; } /* [GREEN-ON-DARK 2026-08-27] */
  i.chev { color: ${(p) => p.theme.muted}; font-size: 11px; margin-left: 2px; }

  &:hover { border-color: ${(p) => p.theme.green600}; }
  &[aria-expanded='true'] {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the date is ONE line, always (a squeezed toolbar wrapped it into a 3-line
     "Oct / 22, / 2026" stack); 10 px sides and 8 px gaps (was 12 / 10) so "Oct 22, 2026" needs about 145 px, a half-width
     cell at 360 px; and when a page stretches the trigger across its cell the arrow sits at the right edge like a
     dropdown's. Desktop unchanged. */
  @media (max-width: 768px) {
    white-space: nowrap;
    padding: 0 10px;
    gap: 8px;
    i.chev { margin-left: auto; }
  }

  /* [LIQUID-GLASS 2026-11-02, user] The glass capsule (the liquid prop): the glass dropdowns' recipe at this button's own 38 px.
     In light it keeps a thin grey edge (white glass alone is lost on a white card); open = the green glow ring. */
  ${(p) => p.$liquid && css`
    position: relative;
    isolation: isolate;
    overflow: hidden;
    border: none;
    border-radius: 999px;
    padding: 0 14px;
    background-color: ${lgControlBg(p)};
    background-image: ${lgCardSheen(p)};
    & > * { position: relative; z-index: 1; }
    ${lgRim}
    ${lgPress}
    box-shadow: ${p.theme.colorScheme === 'dark'
      ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)'
      : '0 0 0 1px rgba(22,25,23,0.13), 0 1px 2px rgba(22,25,23,0.06), 0 8px 18px -10px rgba(22,25,23,0.22)'};
    &:hover:not(:disabled) { background-color: ${lgControlHover(p)}; }
    &[aria-expanded='true'] {
      box-shadow: 0 0 0 1px color-mix(in srgb, ${lgGreen(p)} 70%, transparent), 0 0 0 4px color-mix(in srgb, ${lgGreen(p)} 16%, transparent),
        0 0 18px color-mix(in srgb, ${lgGreen(p)} 24%, transparent);
    }
    @media (max-width: 768px) { padding: 0 10px; }
  `}
`;

/* ───────────────────────── popover ───────────────────────── */

const Pop = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 1300;
  width: 300px;
  /* [PHONE-LAYOUT 2026-10-22] never wider than a phone screen less 8 px each side (the placement clamps it there) */
  @media (max-width: 768px) {
    width: min(300px, calc(100vw - 16px));
  }
  padding: 14px;
  border-radius: 18px;
  /* [LIQUID-GLASS 2026-11-02, user] Glass with the light top edge (styles/liquidGlass.ts). [STALE-PRESS 2026-11-02, user]
     Without the live blur (lgPopover): it opens next to buttons and scrolls. */
  ${lgPopover}
  /* [RESPONSIVE 2026-07-07] Cap to the viewport height (max-height set inline from
     the trigger position) and scroll internally, so the calendar never runs off
     the bottom on a short screen. */
  overflow-y: auto;
  overflow-x: hidden;
  /* [FLUID-1 2026-10-12, user] Grows down out of the trigger's right corner on the spring, the month
     bar and the grid settling in one after another (components/Fluid.tsx); the close plays before it
     unmounts. Replaces the old 0.16 s pop-in. */
  ${(p) => fluidPop({ open: p.$open, origin: '100% 0', from: 'translateY(-8px) scale(0.94)' })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const HeadLabel = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02] The month arrows: round glass buttons (squish + press light), like the panels' close button. */
const NavBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: none;
  color: ${(p) => p.theme.text};
  font-size: 11px;
  cursor: pointer;

  &:hover:not(:disabled) { color: ${(p) => p.theme.green600}; }
  &:disabled { opacity: 0.32; cursor: not-allowed; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const Wk = styled.div`
  text-align: center;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(p) => p.theme.muted};
  padding: 4px 0 6px;
`;

const Day = styled.button<{ $selected?: boolean; $today?: boolean; $disabled?: boolean }>`
  height: 34px;
  border-radius: 11px; /* [LIQUID-GLASS 2026-11-02] rounder; the picked day is glossy (shine + light top edge) */
  border: none;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$selected ? 700 : 500)};
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  color: ${(p) =>
    p.$disabled
      ? p.theme.muted
      : p.$selected
      ? '#fff'
      : p.theme.text};
  opacity: ${(p) => (p.$disabled ? 0.28 : 1)};
  background: ${(p) =>
    p.$selected
      ? `linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700})`
      : 'transparent'};
  box-shadow: ${(p) =>
    p.$selected
      ? `inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 12px -4px ${p.theme.green600}`
      : p.$today
      ? `inset 0 0 0 1.5px ${p.theme.green600}`
      : 'none'};
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${(p) =>
      p.$disabled
        ? 'transparent'
        : p.$selected
        ? `linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700})`
        : p.theme.colorScheme === 'dark'
        ? 'rgba(255,255,255,0.10)'
        : 'rgba(255,255,255,0.85)'};
  }
`;

const Blank = styled.div`
  height: 34px;
`;

const SingleDateCalendar: React.FC<Props> = ({ value, availableDates, min, max, onChange, hint = true, liquid = false }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // [FLUID-8 2026-10-12, user] Opens on the mouse PRESS, like the dropdowns (hooks/usePressAction).
  const openPress = usePressAction<HTMLButtonElement>(() => setOpen((o) => !o));
  const popRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseKey(value), [value]);
  const [view, setView] = useState(() => {
    const p = parseKey(value);
    const now = new Date();
    return p ? { y: p.y, m0: p.m0 } : { y: now.getFullYear(), m0: now.getMonth() };
  });

  // Re-centre the view on the selected month whenever it changes (or on open).
  useEffect(() => {
    if (selected) setView({ y: selected.y, m0: selected.m0 });
  }, [selected]);

  const allowed = useMemo(
    () => (availableDates && availableDates.length ? new Set(availableDates) : null),
    [availableDates]
  );
  const todayKey = useMemo(() => {
    const n = new Date();
    return toKey(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = pageRect(el); // [APP-FIT 2026-10-26] page px (utils/appZoom), like the top / left set below
    // [RESPONSIVE 2026-07-07] Right-align the popover to the trigger (its original
    // look) but clamp it fully inside the viewport so it can never clip off the
    // left/right edge on a narrow screen (mirrors GlassSelect's viewport-clamp).
    // The height is capped in the render (max-height from rect.top), so a short
    // screen scrolls the calendar internally instead of hiding its lower weeks.
    const margin = 8;
    const width = 300; // matches Pop's fixed width below (presence hook: see below the placement)
    let left = r.right - width;
    left = Math.min(left, pageViewport().width - width - margin);
    left = Math.max(left, margin);
    setRect({ top: r.bottom + 6, left });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // [FLUID-1 2026-10-12] Mounted through the 200 ms close so the exit can play; `open` stays the truth.
  const pres = usePresence(open, fluid.outMs);

  // Close on outside-click / Esc; close on scroll+resize so it can't drift away.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = (e: Event) => {
      // [RESPONSIVE 2026-07-07] A scroll INSIDE the popover (a short viewport made
      // the calendar internally scrollable via max-height) must NOT close it — only
      // a scroll of the page behind it should, since that detaches this fixed
      // popover from its trigger. Resize passes the window (not a Node) so it still
      // closes. Mirrors GlassSelect's inside-scroll guard.
      if (e.target instanceof Node && popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const isDisabled = useCallback(
    (key: string) => {
      if (allowed) return !allowed.has(key);
      if (min && key < min) return true;
      if (max && key > max) return true;
      return false;
    },
    [allowed, min, max]
  );

  // Disable month nav once the view reaches the min/max month — no point paging
  // into months that have no selectable day.
  const monthIdx = (y: number, m0: number) => y * 12 + m0;
  const minP = min ? parseKey(min) : null;
  const maxP = max ? parseKey(max) : null;
  const prevDisabled = !!minP && monthIdx(view.y, view.m0) <= monthIdx(minP.y, minP.m0);
  const nextDisabled = !!maxP && monthIdx(view.y, view.m0) >= monthIdx(maxP.y, maxP.m0);

  const step = (dir: -1 | 1) => {
    setView((v) => {
      const d = new Date(v.y, v.m0 + dir, 1);
      return { y: d.getFullYear(), m0: d.getMonth() };
    });
  };

  const firstWeekday = new Date(view.y, view.m0, 1).getDay();
  const daysInMonth = new Date(view.y, view.m0 + 1, 0).getDate();

  const pick = (day: number) => {
    const key = toKey(view.y, view.m0, day);
    if (isDisabled(key)) return;
    onChange(key);
    setOpen(false);
  };

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        $liquid={liquid} /* [LIQUID-GLASS 2026-11-02] */
        {...openPress} /* [FLUID-8 2026-10-12] on the press */
        title={hint ? 'Pick a date' : undefined}
      >
        <i className="cal fas fa-calendar-day" />
        {fmtLong(value)}
        <i className="chev fas fa-chevron-down" />
      </Trigger>

      {pres.mounted && rect && createPortal(
        <Pop ref={popRef} $open={open && pres.entered} style={{ top: rect.top, left: rect.left, maxHeight: `calc(100vh / var(--app-zoom, 1) - ${rect.top + 16}px)` }} role="dialog" aria-label="Choose date">
          <Head>
            <NavBtn type="button" onClick={() => step(-1)} disabled={prevDisabled} aria-label="Previous month">
              <i className="fas fa-chevron-left" />
            </NavBtn>
            <HeadLabel>{MONTHS[view.m0]} {view.y}</HeadLabel>
            <NavBtn type="button" onClick={() => step(1)} disabled={nextDisabled} aria-label="Next month">
              <i className="fas fa-chevron-right" />
            </NavBtn>
          </Head>
          <Grid>
            {WEEKDAYS.map((w, i) => <Wk key={i}>{w}</Wk>)}
            {Array.from({ length: firstWeekday }).map((_, i) => <Blank key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = toKey(view.y, view.m0, day);
              const disabled = isDisabled(key);
              return (
                <Day
                  key={key}
                  type="button"
                  $selected={key === value}
                  $today={key === todayKey}
                  $disabled={disabled}
                  onClick={() => pick(day)}
                  disabled={disabled}
                >
                  {day}
                </Day>
              );
            })}
          </Grid>
        </Pop>,
        document.body
      )}
    </>
  );
};

export default SingleDateCalendar;
